import {
  MAX_SEQUENCE_NUMBER,
  SEQUENCE_MODES,
  formatSequence,
  parseSequenceFormat,
  type SequenceDeclaration,
  type SequenceMode,
  type SequenceState,
} from '@nexus/core';
import {
  Badge,
  Button,
  FormField,
  Input,
  ListSkeleton,
  RadioGroup,
  SettingsSection,
  toast,
} from '@nexus/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminError } from '../admin/shared';
import { api } from '../api/client';

function sequencesQuery(moduleId: string) {
  return {
    queryKey: ['sequences', moduleId],
    queryFn: () => api.get<SequenceState[]>(`/sequences?module=${encodeURIComponent(moduleId)}`),
  };
}

/**
 * Numbering, format and next number of the running numbers of a module, for
 * `<module>.settings.manage` (docs/architecture/sequences.md).
 */
export function SequencesSection({
  moduleId,
  declarations,
}: {
  moduleId: string;
  declarations: readonly SequenceDeclaration[];
}) {
  const { t } = useTranslation();
  const query = useQuery(sequencesQuery(moduleId));
  const labels = new Map(declarations.map((d) => [d.key, d.label]));
  return (
    <SettingsSection title={t('sequences.title')} description={t('sequences.description')}>
      {!query.data ? (
        <ListSkeleton card={false} rows={2} trailing className="py-3" />
      ) : (
        query.data
          .filter((state) => labels.has(state.key))
          .map((state) => (
            <SequenceRow
              key={`${state.key}:${state.mode}:${state.format}:${state.next}`}
              moduleId={moduleId}
              label={t(labels.get(state.key)!)}
              state={state}
            />
          ))
      )}
    </SettingsSection>
  );
}

function SequenceRow({
  moduleId,
  label,
  state,
}: {
  moduleId: string;
  label: string;
  state: SequenceState;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const reportError = useAdminError();
  const modeLabel = useId();
  const [mode, setMode] = useState<SequenceMode>(state.mode);
  const [format, setFormat] = useState(state.format);
  const [next, setNext] = useState(String(state.next));

  // Typed-in numbers need no format; changes to it wait until the switch back.
  const auto = mode === 'auto';
  const parsed = parseSequenceFormat(format.trim());
  const number = /^\d+$/.test(next) ? Number(next) : NaN;
  const nextValid = Number.isSafeInteger(number) && number >= 1 && number <= MAX_SEQUENCE_NUMBER;
  // A new format may switch to another counter, the server then checks again.
  const lower = nextValid && format.trim() === state.format && number < state.next;
  const formatChanged = auto && format.trim() !== state.format;
  const nextChanged = auto && number !== state.next;
  const dirty = mode !== state.mode || formatChanged || nextChanged;
  const valid = !auto || (parsed !== null && nextValid && !lower);

  const save = useMutation({
    mutationFn: () =>
      api.patch<SequenceState>(`/sequences/${encodeURIComponent(state.key)}`, {
        ...(mode !== state.mode ? { mode } : {}),
        ...(formatChanged ? { format: format.trim() } : {}),
        ...(nextChanged ? { next: number } : {}),
      }),
    onSuccess: () => toast.success(t('sequences.saved')),
    onError: (error) =>
      reportError(error, {
        'sequence-lower': t('sequences.nextLower', { current: state.next }),
      }),
    onSettled: async () => {
      await client.invalidateQueries({ queryKey: ['sequences', moduleId] });
      await client.invalidateQueries({ queryKey: ['sequence-mode', state.key] });
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (valid && dirty) save.mutate();
  }

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-3 py-4">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-medium text-fg">{label}</h3>
          {(state.format !== state.defaultFormat || state.mode !== 'auto') && (
            <Badge tone="brand">{t('sequences.changed')}</Badge>
          )}
        </div>
        <p className="mt-0.5 text-fg-muted" aria-live="polite">
          {!auto
            ? t('sequences.previewManual')
            : t(
                (parsed ?? parseSequenceFormat(state.format))?.random
                  ? 'sequences.previewRandom'
                  : 'sequences.preview',
                {
                  preview:
                    parsed && nextValid
                      ? formatSequence(parsed, number, new Date())
                      : state.preview,
                },
              )}
        </p>
      </div>
      {state.manualEntry && (
        <div>
          <p id={modeLabel} className="mb-1.5 font-medium text-fg">
            {t('sequences.mode')}
          </p>
          <RadioGroup
            aria-labelledby={modeLabel}
            value={mode}
            onValueChange={(value) => setMode(value as SequenceMode)}
            options={SEQUENCE_MODES.map((value) => ({
              value,
              label: t(`sequences.modes.${value}.label`),
              description: t(`sequences.modes.${value}.description`),
            }))}
          />
        </div>
      )}
      {auto && (
        <div className="grid gap-3 md:grid-cols-2">
          <FormField
            label={t('sequences.format')}
            description={t('sequences.formatHelp')}
            error={parsed ? undefined : t('sequences.formatInvalid')}
          >
            {(field) => (
              <Input
                {...field}
                value={format}
                maxLength={40}
                spellCheck={false}
                autoComplete="off"
                onChange={(event) => setFormat(event.target.value)}
              />
            )}
          </FormField>
          <FormField
            label={t('sequences.next')}
            error={
              !nextValid
                ? t('sequences.nextInvalid')
                : lower
                  ? t('sequences.nextLower', { current: state.next })
                  : undefined
            }
          >
            {(field) => (
              <Input
                {...field}
                inputMode="numeric"
                value={next}
                onChange={(event) => setNext(event.target.value.trim())}
              />
            )}
          </FormField>
        </div>
      )}
      <div className="flex justify-end">
        <Button type="submit" size="sm" loading={save.isPending} disabled={!dirty || !valid}>
          {t('sequences.save')}
        </Button>
      </div>
    </form>
  );
}
